import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Package, Truck, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { formatPrice } from '../utils/currency';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderNumber = searchParams.get('order_number');
  
  const [loading, setLoading] = useState(true);
  const [verifyingPayment, setVerifyingPayment] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (orderNumber) {
      // Direct order number provided (from backend callback)
      fetchOrderByNumber();
    } else if (sessionId) {
      // Session ID provided (from Stripe redirect) - use webhook + polling approach
      pollForPaymentConfirmation();
    } else {
      setError(t('checkoutSuccess.errorNoOrderInfo'));
      setLoading(false);
    }
  }, [sessionId, orderNumber]);

  const pollForPaymentConfirmation = async () => {
    // Poll every 2 seconds for up to 30 seconds (15 attempts)
    const maxAttempts = 15;
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        setPollCount(attempts);

        // Check payment status
        const statusResponse = await fetch(`${API_URL}/api/checkout/payment-status/${sessionId}`);
        
        if (!statusResponse.ok) {
          throw new Error(t('checkoutSuccessMore.errorVerifyPayment'));
        }

        const statusData = await statusResponse.json();
        
        // If payment is confirmed, fetch order details
        if (statusData.payment_status === 'paid' && statusData.order_number) {
          setVerifyingPayment(false);
          setPaymentStatus(statusData);
          
          // Fetch full order details
          const orderResponse = await fetch(`${API_URL}/api/checkout/order/${statusData.order_number}?session_id=${encodeURIComponent(sessionId)}`);
          if (orderResponse.ok) {
            const orderData = await orderResponse.json();
            setOrder(orderData);
            
            // Clear cart
            const sessionCartId = localStorage.getItem('sessionId');
            if (sessionCartId) {
              await fetch(`${API_URL}/api/cart`, {
                method: 'DELETE',
                headers: { 'X-Session-ID': sessionCartId }
              });
              localStorage.removeItem('sessionId');
            }
          }
          
          setLoading(false);
        } else if (attempts < maxAttempts) {
          // Payment not confirmed yet, poll again
          setTimeout(poll, 2000);
        } else {
          // Max attempts reached
          setError(t('checkoutSuccess.errorVerificationTimeout'));
          setLoading(false);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        if (attempts < maxAttempts) {
          // Retry on error
          setTimeout(poll, 2000);
        } else {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    // Start polling
    poll();
  };

  const fetchOrderByNumber = async () => {
    try {
      // Backend already verified payment, just fetch and display order
      const orderResponse = await fetch(`${API_URL}/api/checkout/order/${orderNumber}${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`, {
        headers: localStorage.getItem('accessToken') ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } : {},
      });
      
      if (!orderResponse.ok) {
        throw new Error(t('checkoutSuccessMore.errorLoadOrder'));
      }

      const orderData = await orderResponse.json();
      setOrder(orderData);
      
      // Set payment status for UI display
      setPaymentStatus({
        payment_status: 'paid',
        amount: orderData.total,
        currency: orderData.currency
      });

      // Clear cart
      const sessionCartId = localStorage.getItem('sessionId');
      if (sessionCartId) {
        await fetch(`${API_URL}/api/cart`, {
          method: 'DELETE',
          headers: { 'X-Session-ID': sessionCartId }
        });
        localStorage.removeItem('sessionId');
      }
    } catch (err) {
      console.error('Order fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async () => {
    try {
      // Check payment status
      const statusResponse = await fetch(`${API_URL}/api/checkout/payment-status/${sessionId}`);
      
      if (!statusResponse.ok) {
        throw new Error(t('checkoutSuccessMore.errorVerifyPayment'));
      }

      const statusData = await statusResponse.json();
      setPaymentStatus(statusData);

      // If payment successful, fetch order details
      if (statusData.payment_status === 'paid' && statusData.order_number) {
        // Clear cart
        const sessionCartId = localStorage.getItem('sessionId');
        if (sessionCartId) {
          await fetch(`${API_URL}/api/cart`, {
            method: 'DELETE',
            headers: { 'X-Session-ID': sessionCartId }
          });
          localStorage.removeItem('sessionId');
        }

        // Fetch order details using order_number from payment status
        const orderResponse = await fetch(`${API_URL}/api/checkout/order/${statusData.order_number}?session_id=${encodeURIComponent(sessionId)}`);
        if (orderResponse.ok) {
          const orderData = await orderResponse.json();
          setOrder(orderData);
        }
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          {verifyingPayment && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#be1f1a] mx-auto mb-6"></div>
              <h2 className="text-2xl font-serif text-[#2C2420] mb-3">{t('checkoutSuccess.verifyingPayment')}</h2>
              <p className="text-gray-600 mb-2">
                {t('checkoutSuccess.verifyingPaymentDescription')}
              </p>
              <p className="text-sm text-gray-500">
                {t('checkoutSuccess.verifyingPaymentHint')} {pollCount > 0 && `(${t('checkoutSuccess.verifyingAttempt', { n: pollCount })})`}
              </p>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 {t('checkoutSuccess.tipDontClose')}
                </p>
              </div>
            </>
          )}
          {!verifyingPayment && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#be1f1a] mx-auto mb-4"></div>
              <p className="text-gray-600">{t('checkout.loadingOrderDetails')}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-serif text-[#2C2420] mb-2">{t('checkoutSuccess.paymentError')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/shop">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {t('checkoutSuccess.returnToShop')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (paymentStatus?.payment_status === 'paid') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <SEOHead
          title={order?.order_number
            ? `${t('checkoutSuccess.orderConfirmed')} — #${order.order_number}`
            : t('checkoutSuccess.orderConfirmed')}
        />
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="bg-white rounded-lg shadow-lg p-8 text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-serif text-[#2C2420] mb-2">{t('checkoutSuccess.orderConfirmed')}</h1>
            <p className="text-gray-600 mb-1">{t('checkoutSuccess.thankYou')}</p>
            {order && (
              <p className="text-sm text-gray-500">
                {t('checkoutSuccess.orderNumberLabel', { n: order.order_number })}
              </p>
            )}
          </div>

          {/* Order Status */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold text-[#2C2420] mb-4">{t('checkoutSuccess.whatsNext')}</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2420]">{t('checkoutSuccess.paymentConfirmed')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('checkoutSuccess.paymentConfirmedDescription', { amount: formatPrice(paymentStatus.amount, paymentStatus.currency) })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2420]">{t('checkoutSuccess.orderProcessing')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('checkoutSuccess.orderProcessingDescription')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C2420]">{t('checkoutSuccess.shippingSoon')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('checkoutSuccess.shippingSoonDescription')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Details */}
          {order && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-semibold text-[#2C2420] mb-4">{t('checkoutSuccess.orderDetails')}</h2>

              {/* Items */}
              <div className="space-y-4 mb-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <img
                      src={item.product_image || 'https://placehold.co/80x100'}
                      alt={item.product_name}
                      className="w-20 h-24 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-[#2C2420]">{item.product_name}</h4>
                      <p className="text-sm text-gray-600">{t('checkoutSuccess.quantity')}: {item.quantity}</p>
                      <p className="text-sm font-semibold text-[#be1f1a]">
                        {formatPrice(item.price * item.quantity, item.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkoutSuccess.subtotal')}:</span>
                  <span>{formatPrice(order.subtotal, order.currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.shipping')}:</span>
                  <span>{formatPrice(order.shipping_cost, order.currency)}</span>
                </div>

                {/* Display discount if applied */}
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>{t('checkout.discount')}:</span>
                    <span>-{formatPrice(order.discount_amount, order.currency)}</span>
                  </div>
                )}

                {/* Display tax if present */}
                {order.tax_info && order.tax_info.tax_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {order.tax_info.tax_name} ({order.tax_info.tax_rate}%):
                      {order.tax_info.included_in_price && (
                        <span className="text-xs ml-1">({t('checkout.taxIncluded').toLowerCase()})</span>
                      )}
                    </span>
                    <span>
                      {order.tax_info.included_in_price ? (
                        <span className="text-gray-500">{t('checkout.taxIncluded')}</span>
                      ) : (
                        formatPrice(order.tax_info.tax_amount, order.currency)
                      )}
                    </span>
                  </div>
                )}

                <Separator />
                <div className="flex justify-between text-lg font-semibold">
                  <span>{t('checkoutSuccess.total')}:</span>
                  <span className="text-[#be1f1a]">{formatPrice(order.total, order.currency)}</span>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Shipping Address */}
              <div>
                <h3 className="font-semibold text-[#2C2420] mb-2">{t('checkoutSuccess.shippingAddressTitle')}</h3>
                {order.shipping_method?.id === 'pickup' ? (
                  <div className="text-sm text-gray-600" data-testid="success-pickup">
                    <p className="font-medium">{order.shipping_method.name}</p>
                    <p>{t('checkoutSuccess.pickupInfo')}</p>
                  </div>
                ) : (
                <div className="text-sm text-gray-600">
                  <p>{order.shipping_address.full_name}</p>
                  <p>{order.shipping_address.street} {order.shipping_address.number}</p>
                  {order.shipping_address.apartment && <p>{order.shipping_address.apartment}</p>}
                  {order.shipping_address.district && <p>{order.shipping_address.district}</p>}
                  <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
                  <p>{order.shipping_address.country}</p>
                </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/account/orders" className="flex-1">
              <Button className="w-full bg-[#be1f1a] hover:bg-[#a71b17]">
                {t('checkoutSuccess.viewOrders')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/shop" className="flex-1">
              <Button variant="outline" className="w-full border-[#be1f1a] text-[#be1f1a] hover:bg-[#be1f1a] hover:text-white">
                {t('checkout.continueShopping')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-yellow-600" />
        </div>
        <h1 className="text-2xl font-serif text-[#2C2420] mb-2">{t('checkoutSuccess.paymentPending')}</h1>
        <p className="text-gray-600 mb-6">
          {t('checkoutSuccess.paymentPendingDescription')}
        </p>
        <Link to="/">
          <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
            {t('checkoutSuccess.returnHome')}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
